import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pluralRu, toast } from '@wit-support/ui';
import { bulkConversationAction } from '../api.js';
import { bulkSelection } from '../hooks/use-bulk-selection.js';
import { INBOX_LAST_CONVERSATION_KEY } from '../hooks/use-conversation-status-mutation.js';
/** Machine reason code → operator-facing RU label (unknown codes pass through). */
const REASON_LABEL_RU = {
    pipeline_not_eligible: 'пайплайн не подходит',
    pipeline_already_running: 'пайплайн уже выполняется',
    pipeline_already_ran: 'уже отработал (письмо отправлено)',
    already_mailed: 'этому клиенту письмо уже уходило',
    same_contact_in_batch: 'тот же клиент уже в этом прогоне',
    status_not_runnable: 'статус вне пакетного прогона',
    non_customer_sender: 'пишет не частный клиент (бизнес)',
    not_found: 'переписка не найдена',
    template_not_found: 'шаблон не найден',
    merged: 'переписка объединена',
    reply_data_unavailable: 'нет данных для шаблона',
    invalid_schedule_time: 'неверное время',
    schedule_too_soon: 'время слишком близко',
    idempotency_key_consumed: 'расписание уже завершено',
    emit_failed: 'ошибка постановки в очередь',
    status_update_failed: 'не удалось сменить статус',
    no_recipient: 'нет адресата',
    channel_not_found: 'канал не найден',
    channel_cannot_send: 'канал не может отправить',
    send_failed_retryable: 'ошибка отправки',
    send_failed_permanent: 'ошибка отправки',
    duplicate_send_pending: 'дубликат уже в очереди',
    recipient_blacklisted: 'адрес в чёрном списке',
    unexpected_error: 'непредвиденная ошибка',
};
/**
 * PRE-FLIGHT refusal codes — the endpoint rejected the whole request before the
 * per-item loop ran, so nothing was written — mapped to an operator-facing
 * sentence. Membership is load-bearing twice over: `extractServerError` surfaces
 * the raw `error` field (a bare `channel_unhealthy` tells the operator nothing
 * about what to fix), and `onError` keeps the selection for exactly these codes
 * because they carry no ambiguity. Unknown codes pass through unchanged and are
 * treated as ambiguous.
 */
const REQUEST_ERROR_LABEL_RU = {
    channel_unhealthy: 'Канал связи неисправен — пакетный прогон заблокирован. Проверьте экран «Каналы», ' +
        'почините канал и повторите. Ничего не отправлено.',
    pipelines_not_configured: 'Пайплайны не настроены на сервере.',
    pipeline_definition_invalid: 'Граф пайплайна не читается — прогон заблокирован. Ничего не отправлено.',
    reply_not_configured: 'Отправка ответов не настроена на сервере.',
    scheduled_email_not_configured: 'Отложенная отправка не настроена на сервере.',
    status_update_not_configured: 'Смена статуса не настроена на сервере.',
};
/** Group skipped/failed rows by reason → a sorted «label — N» breakdown. */
export function reasonBreakdown(results) {
    const counts = new Map();
    for (const r of results) {
        if (r.ok)
            continue;
        const code = r.reason ?? 'unexpected_error';
        counts.set(code, (counts.get(code) ?? 0) + 1);
    }
    return [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([code, n]) => `${REASON_LABEL_RU[code] ?? code} — ${n}`)
        .join('\n');
}
/**
 * Shared mutation behind every bulk dialog (status / pipeline / reply /
 * schedule). It POSTs ONE action over the selected conversation ids, then on
 * success:
 *   - toasts the aggregate summary, with a per-reason breakdown in the
 *     description when anything was skipped or failed (so the operator sees WHY,
 *     not just counts),
 *   - invalidates the broad `['conversations']` list query (and, best-effort,
 *     each acted `['conversation', id]`),
 *   - calls `onDone` so the caller can clear the selection, exit bulk mode, and
 *     close the dialog.
 * On a full failure (non-2xx — bad body / unconfigured dependency) it toasts the
 * error and leaves the selection intact so the operator can retry.
 *
 * PREVIEW (`dryRun`): the same mutation, short-circuited before any of that —
 * counts only, no toast, no selection change, no invalidation. Sharing the
 * mutation keeps one fetch, one error path and one idempotency policy for both
 * the count and the run it precedes.
 *
 * IDEMPOTENCY: each logical attempt pins ONE client-side `idempotency-key`
 * (held in a ref). A react-query retry or an operator re-submit after an
 * ambiguous failure reuses the same key, so the server collapses duplicate
 * `send_reply`/`schedule_email` writes instead of double-sending. The key is
 * rolled only after a successful response — a fresh logical action gets a fresh
 * key.
 */
export function useBulkAction(onDone) {
    const queryClient = useQueryClient();
    const idempotencyKeyRef = useRef(null);
    return useMutation({
        mutationFn: ({ conversationIds, action, dryRun, includeAlreadyRan }) => {
            // A preview writes nothing, so it neither needs nor consumes an
            // idempotency key — allocating one here would burn the key the following
            // real run is supposed to pin.
            if (!dryRun && !idempotencyKeyRef.current)
                idempotencyKeyRef.current = crypto.randomUUID();
            return bulkConversationAction({
                conversationIds: conversationIds,
                action,
                ...(dryRun ? { dryRun: true } : {}),
                ...(includeAlreadyRan ? { includeAlreadyRan: true } : {}),
            }, idempotencyKeyRef.current ? { idempotencyKey: idempotencyKeyRef.current } : undefined);
        },
        onSuccess: (res, vars) => {
            const { ok, skipped, failed } = res.summary;
            // ── PREVIEW ────────────────────────────────────────────────────────────
            // A dry run must leave every piece of live state alone: no selection
            // narrowing, no bulk-mode exit, no cache invalidation, no «Готово» toast.
            // It only hands the counts back to the caller.
            if (vars.dryRun) {
                if (res.dryRun !== true) {
                    // The server did NOT recognise the flag — Zod strips unknown keys, so
                    // this response is the result of a REAL run, not a count. Say so; the
                    // damage is already done and hiding it behind a plausible-looking
                    // preview would be worse.
                    toast.error('Сервер не поддерживает предпросмотр — прогон мог быть выполнен по-настоящему', {
                        description: 'Обновите admin-api и проверьте историю прогонов у выбранных диалогов.',
                    });
                    return;
                }
                onDone(res, vars);
                return;
            }
            // The endpoint returns 200 even on partial failure. We NARROW the selection
            // to just the failed ids (excluding deliberate `skipped` no-ops) so a retry
            // can only ever touch the failures — never the already-succeeded rows. This
            // is the load-bearing safety property: keeping the full selection would let
            // an EDITED retry (different template / time) re-contact succeeded customers
            // a second time. Narrowing makes that impossible.
            //
            // We deliberately do NOT sub-classify failures by reason (that proved a
            // brittle, endless source of edge cases). A genuinely terminal failure
            // (not_found / merged / template_not_found) simply stays in the narrowed set
            // and re-fails if retried — an operator-visible, fully-recoverable cosmetic
            // (the per-reason toast names it; ✕/«Снять» exit), NOT a correctness bug.
            const failedIds = res.results
                .filter((r) => !r.ok && !r.skipped)
                .map((r) => r.conversationId);
            if (failedIds.length > 0) {
                // KEEP the idempotency key so a retry of the narrowed set reuses it (the
                // server dedups any item that did partially commit); narrow the selection.
                bulkSelection.selectAll(failedIds);
            }
            else {
                // Nothing failed → roll the key (next action is fresh). Exit bulk mode on a
                // real success; an all-skipped result keeps the selection + bulk mode so the
                // operator can pick a different action instead of being silently kicked out.
                idempotencyKeyRef.current = null;
                if (ok > 0)
                    bulkSelection.exit();
            }
            const msg = `Готово: ${ok} ${pluralRu(ok, ['успешная', 'успешных', 'успешных'])} · ` +
                `${skipped} ${pluralRu(skipped, ['пропущена', 'пропущено', 'пропущено'])} · ` +
                `${failed} ${pluralRu(failed, ['ошибка', 'ошибки', 'ошибок'])}`;
            const breakdown = skipped + failed > 0 ? reasonBreakdown(res.results) : '';
            const opts = breakdown ? { description: breakdown } : undefined;
            if (failed > 0 && ok === 0) {
                toast.error(msg, opts);
            }
            else if (failed + skipped > 0) {
                toast.warning(msg, opts);
            }
            else {
                toast.success(msg);
            }
            void queryClient.invalidateQueries({ queryKey: ['conversations'] });
            // Best-effort refresh of each acted conversation's detail cache so an open
            // thread reflects the change without waiting for the inbox poll. We also
            // refresh the action-specific caches the single-conversation paths
            // invalidate, so a bulk action on the currently-open conversation never
            // looks like it did nothing:
            //   • send_reply     → ['messages', id]                       (thread)
            //   • schedule_email → ['scheduled-emails', id]               (queue panel)
            //   • run_pipeline   → ['pipeline-runs', id]                  (runs panel)
            const sentReply = vars.action.type === 'send_reply';
            const scheduledEmail = vars.action.type === 'schedule_email';
            const ranPipeline = vars.action.type === 'run_pipeline';
            for (const id of vars.conversationIds) {
                void queryClient.invalidateQueries({ queryKey: ['conversation', id] });
                if (sentReply) {
                    void queryClient.invalidateQueries({ queryKey: ['messages', id] });
                    // A template send also cancels any pending duplicate-template scheduled
                    // email (cancelByTemplateInConversation), so refresh the queue panel too.
                    void queryClient.invalidateQueries({ queryKey: ['scheduled-emails', id] });
                }
                if (scheduledEmail) {
                    void queryClient.invalidateQueries({ queryKey: ['scheduled-emails', id] });
                }
                if (ranPipeline) {
                    void queryClient.invalidateQueries({ queryKey: ['pipeline-runs', id] });
                }
            }
            // Parity with the single-conversation status mutation: finishing a chat
            // (resolved/closed) forgets it as the remembered last-open conversation so
            // returning to the inbox doesn't auto-reopen a thread just wrapped up.
            if (vars.action.type === 'change_status' &&
                (vars.action.status === 'resolved' || vars.action.status === 'closed')) {
                try {
                    const remembered = localStorage.getItem(INBOX_LAST_CONVERSATION_KEY);
                    const okIds = new Set(res.results.filter((r) => r.ok).map((r) => r.conversationId));
                    if (remembered && okIds.has(remembered)) {
                        localStorage.removeItem(INBOX_LAST_CONVERSATION_KEY);
                    }
                }
                catch {
                    /* best-effort */
                }
            }
            onDone(res, vars);
        },
        onError: (err) => {
            // A non-2xx / network failure is AMBIGUOUS: the server may have already
            // committed some of the per-conversation sends/schedules before the response
            // was lost, and — unlike the 200 partial-failure path — we get no per-item
            // results to narrow to. So we CLEAR the selection (a deliberate re-select is
            // now required before any retry) while KEEPING the idempotency key. This
            // closes the double-contact hole: a blind edited retry (different template or
            // time) over the retained full selection would mint fresh per-conversation
            // keys and re-contact every already-committed customer. With the key kept, a
            // deliberate same-template re-select still dedups the committed items; an
            // edited retry becomes an informed fresh choice over the rows the operator
            // re-picks. Bulk mode stays on so re-selecting is one step away.
            //
            // EXCEPT for the PRE-FLIGHT refusals below: those are emitted before the
            // per-item loop touches anything, so they are not ambiguous at all —
            // nothing committed. Clearing a 164-row selection because a channel is red
            // would cost the operator the whole re-select for no safety gain, so the
            // selection survives and the toast says what to fix.
            const preflight = REQUEST_ERROR_LABEL_RU[err.message];
            if (!preflight)
                bulkSelection.clear();
            toast.error(preflight ?? err.message);
        },
    });
}
//# sourceMappingURL=use-bulk-action.js.map